import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { ErrorMessage, InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { CurrencyInput } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';
import { Label } from '@eika/label';
import { Select } from '@eika/select';

import {
    FormTypeCalculatorVehicleInfo,
    FormTypeEuroTaxVehicleInfo,
    fetchEuroTaxLoanCommitment,
    fetchOngoingApplicationEuroTax,
    fetchOngoingApplicationEuroTaxCleared,
} from '../../actions';
import Loader from '../../components/Loader/Loader';
import { UpgradeToVehicleLoanRequest } from '../../generated-types';
import { AppState } from '../../reducers';
import { CustomerCaseInfoLandingPageUpdate } from '../../reducers/ongoingapplications';
import { errorNames } from '../../util/mappings/errorName';
import LoanProduct from '../../util/mappings/loanProduct';
import LoanProductRegValidations, { LoanProductRegValid } from '../../util/mappings/loanProductRegValidations';
import { euroTaxSchema } from '../../validation/loanEuroTaxSchema';

interface Props {
    application: CustomerCaseInfoLandingPageUpdate;
}

const LoanUpdateForm = ({ application }: Props): JSX.Element => {
    const dispatch = useDispatch<any>();
    const { vehicle, context, ongoingApplications, loader } = useSelector((state: AppState) => state);
    const { data } = context.config;
    const { text } = data;
    const { applicationId, loanProduct, loanAmount } = application;
    const { selectedInterestsAndFees, selectedError } = ongoingApplications;
    const eurotaxPlateNotFound = selectedError && selectedError === errorNames.eurotaxPlateNotFound;

    const { manufacturer, registrationNumber, manufactureYear, importedUsed, make, co2, fuel } = vehicle.vehicleDetails;
    const regNrValidateObject: LoanProductRegValid = LoanProductRegValidations(loanProduct);
    const vehicleRegistrationNumberLength = regNrValidateObject.max;

    const MAX_DEFAULT_DURATION = 10;
    const DEFAULT_DURATION = 14;
    const NO_INSURACE_DURATION = 5;
    const ELECTRIC_FUEL = 'ELEKTRISITET';
    const HYDROGEN_FUEL = 'HYDROGEN';

    let vehicleTooOld = false;
    let vehicleHasEmmision = false;

    const vehicleInfo = `${manufacturer} ${make && make !== text.eurotaxUnknown ? make : ''} ${manufactureYear}${
        importedUsed ? ` ${text.eurotaxImported}` : ''
    }`;

    let loanAmountMin = 30000;
    let loanAmountMax = 2000000;

    let maxAge: number | null = null;
    const currentYear = new Date().getFullYear();
    const isCaravan = loanProduct === LoanProduct.campingCaravan;

    if (selectedInterestsAndFees) {
        ({ loanAmountMin, loanAmountMax } = selectedInterestsAndFees);
    }

    if (loanProduct !== LoanProduct.carNoInsurance) {
        loanAmountMax = loanAmountMax > loanAmount ? loanAmount : loanAmountMax;
    }

    if (manufactureYear) {
        if (currentYear - manufactureYear <= 4) {
            maxAge = MAX_DEFAULT_DURATION;
        } else {
            maxAge = DEFAULT_DURATION - (currentYear - manufactureYear);
        }
    } else {
        maxAge = MAX_DEFAULT_DURATION;
    }

    maxAge = loanProduct === LoanProduct.carNoInsurance ? NO_INSURACE_DURATION : maxAge;

    if (loanProduct === LoanProduct.carElectric) {
        if (fuel !== ELECTRIC_FUEL && fuel !== HYDROGEN_FUEL) {
            vehicleHasEmmision = true;
        } else {
            vehicleHasEmmision = !(co2 === '' || co2 === '0000');
        }
    }

    vehicleTooOld = !!(manufactureYear && currentYear - manufactureYear > DEFAULT_DURATION);
    const shortMode = vehicleTooOld || vehicleHasEmmision;

    const getMonths = () => {
        const opts = [];
        const duration = maxAge;
        if (duration) {
            for (let i = 1; i < duration + 1; i += 1) {
                const id = `options_${i}`;
                opts.push(
                    <option value={i} key={id}>
                        {i} {text.eurotaxDurationYear}
                    </option>,
                );
            }
        }
        return opts;
    };

    const loanEquityAmountErrorText = `${text.eurotaxLoanEquityAmountMessage1} ${ValutaFilter(
        loanAmountMin,
        ' ',
        0,
    )} og ${ValutaFilter(loanAmountMax, ' ', 0)}${text.eurotaxLoanEquityAmountMessage2}`;

    const {
        handleSubmit,
        register,
        setValue,
        trigger,
        formState: { errors },
    } = useForm<FormTypeEuroTaxVehicleInfo>({
        resolver: yupResolver(
            euroTaxSchema(
                loanAmountMin,
                loanAmountMax,
                shortMode,
                loanEquityAmountErrorText,
                text.eurotaxLoanEquityAmountMessage1,
                regNrValidateObject,
                isCaravan,
            ),
        ),
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {
            vehiclePrice: null,
            regNr: registrationNumber,
        },
    });

    const mergeData = (form: FormTypeEuroTaxVehicleInfo) => {
        const payload = { ...form } as UpgradeToVehicleLoanRequest;
        payload.preViewCaseId = applicationId;
        payload.imported = !!importedUsed;
        payload.manufacturer = manufacturer || '';
        payload.model = make && make !== text.eurotaxUnknown ? make : null;
        payload.loanProduct = loanProduct;
        payload.odometerValue = form.odometerValue ? form.odometerValue : 0;

        if (manufactureYear) {
            payload.modelYear = manufactureYear;
        }
        if (form.loanPeriodInMonths) {
            payload.loanPeriodInMonths = form.loanPeriodInMonths * 12;
        }
        return payload;
    };

    const disableRegNumber = () => {
        if (vehicleHasEmmision) {
            return false;
        }

        return !!registrationNumber && !vehicleTooOld;
    };

    if (loader.isFetchingVehicleInfo || loader.isUpgradingOngoingApplication)
        return (
            <div className="vehicle-info eurotax-search-form">
                <Loader title={text.loadingDefault} />
            </div>
        );

    return (
        <>
            <div className="vehicle-info eurotax-full-form">
                <form
                    onSubmit={handleSubmit((form: FormTypeEuroTaxVehicleInfo) => {
                        if (vehicleTooOld || vehicleHasEmmision) {
                            dispatch(fetchOngoingApplicationEuroTaxCleared(applicationId));
                            dispatch(
                                fetchOngoingApplicationEuroTax(
                                    {
                                        vehicleRegistrationNumber: form.regNr,
                                    } as FormTypeCalculatorVehicleInfo,
                                    applicationId,
                                ),
                            );
                        } else {
                            dispatch(fetchEuroTaxLoanCommitment(applicationId, mergeData(form)));
                        }
                    })}
                >
                    <InfoMessage
                        className="loan-info__info-message"
                        icon={IconTypes.LYSPAERE_24}
                        text={text.eurotaxSearchErrorText1}
                    />

                    {vehicleInfo && (
                        <div className="vehicle-info__result">
                            <Label text={vehicleInfo} />
                        </div>
                    )}
                    <SearchInput
                        {...register('regNr')}
                        label={text.eurotaxRegNumber}
                        type="text"
                        minLength={vehicleRegistrationNumberLength}
                        error={errors?.regNr?.message || ''}
                        disabled={disableRegNumber()}
                        onChange={(e: any) => {
                            setValue(
                                e.target.name,
                                e.target.value
                                    .replace(/[^\d\w]/g, '')
                                    .substring(0, vehicleRegistrationNumberLength)
                                    .toUpperCase(),
                            );
                        }}
                    />
                    {vehicleHasEmmision && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.eurotaxEmissionWarning}
                        />
                    )}
                    {!vehicleTooOld && !vehicleHasEmmision && (
                        <>
                            <CurrencyInput
                                {...register('vehiclePrice')}
                                label={text.eurotaxPrice}
                                placeholder={text.eurotaxEquityPlaceholder}
                                error={errors?.vehiclePrice?.message || ''}
                                prefix=""
                            />

                            <CurrencyInput
                                {...register('equity')}
                                label={text.eurotaxEquity}
                                placeholder={text.eurotaxEquityPlaceholder}
                                error={errors?.equity?.message || ''}
                                onChange={(e) => {
                                    setValue('equity', Number(e.target.value));
                                    trigger('vehiclePrice');
                                }}
                                prefix=""
                            />
                            <Select
                                {...register('loanPeriodInMonths')}
                                label={text.eurotaxDuration}
                                error={errors?.loanPeriodInMonths?.message || ''}
                                id="loanPeriodInMonths"
                                defaultValue=""
                            >
                                <option value="">{text.eurotaxSelectDurationPlaceholder}</option>
                                {getMonths()}
                            </Select>
                            <InfoMessage
                                className="loan-info__info-message"
                                icon={IconTypes.LYSPAERE_24}
                                text={text.eurotaxDurationText}
                            />
                            {!isCaravan && (
                                <CurrencyInput
                                    {...register('odometerValue')}
                                    label={text.calculatorVehicleInfoLabelOdometerValue}
                                    placeholder={text.eurotaxOdometerPlaceholder}
                                    error={errors?.odometerValue?.message || ''}
                                    max={100000000000}
                                    prefix=""
                                    suffix=" km"
                                />
                            )}
                        </>
                    )}

                    <div className="vehicle-submit__button">
                        <StandardButton type="submit">
                            {!vehicleTooOld ? text.eurotaxSubmitButton : text.eurotaxSearchButton}
                        </StandardButton>
                    </div>
                    {vehicleTooOld && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.calculatorVehicleInfoErrorObjectAge}
                        />
                    )}
                    {eurotaxPlateNotFound && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.eurotaxPlateNotFoundWarning}
                        />
                    )}
                </form>
            </div>
        </>
    );
};
export default LoanUpdateForm;
